import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from './useUser';
import { 
  detectLanguageForGuest, 
  initializeLanguage, 
  saveLanguageToStorage, 
  getLanguageFromStorage,
  convertToI18nFormat,
  convertFromI18nFormat
} from '../utils/languageDetection';

export const useLanguage = () => {
  const { i18n } = useTranslation();
  const { user, loading: userLoading } = useUser();
  const [currentLanguage, setCurrentLanguage] = useState<'RUS' | 'ENG'>('RUS');
  const [isInitialized, setIsInitialized] = useState(false);

  // Инициализация языка при загрузке
  useEffect(() => {
    if (userLoading || isInitialized) return; // Ждем загрузки пользователя и не переинициализируем

    const initializeAppLanguage = () => {
      const isAuthenticated = !!user;
      const userLanguage = user?.preferred_language || null;
      
      // Определяем приоритетный язык
      const detectedLanguage = initializeLanguage(userLanguage, isAuthenticated);
      
      // Конвертируем в формат i18n
      const i18nLanguage = convertToI18nFormat(detectedLanguage);
      
      // Устанавливаем язык в i18n
      i18n.changeLanguage(i18nLanguage);
      setCurrentLanguage(i18nLanguage);
      
      // Сохраняем в localStorage для гостей (если еще не сохранен)
      if (!isAuthenticated) {
        const storedLanguage = getLanguageFromStorage();
        if (!storedLanguage) {
          saveLanguageToStorage(detectedLanguage);
        }
      }
      
      setIsInitialized(true);
    };

    initializeAppLanguage();
  }, [user, userLoading, i18n, isInitialized]);

  // Синхронизация с i18n при изменении
  useEffect(() => {
    setCurrentLanguage(i18n.language === 'ENG' ? 'ENG' : 'RUS');
  }, [i18n.language]);

  // Функция переключения языка
  const switchLanguage = async (newLanguage: 'RUS' | 'ENG') => {
    const languageCode = convertFromI18nFormat(newLanguage);
    
    // Сохраняем в localStorage
    saveLanguageToStorage(languageCode);
    
    // Если пользователь авторизован, сохраняем в БД
    if (user?.user_id) {
      try {
        const apiBase = process.env.REACT_APP_API_URL;
        if (apiBase) {
          const base = apiBase.replace(/\/$/, '');
          const url = base.endsWith('/api') ? `${base}/user/language` : `${base}/api/user/language`;
          
          await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: parseInt(user.user_id),
              language: languageCode
            })
          });
        }
      } catch (error) {
        console.warn('Не удалось сохранить язык в БД:', error);
      }
    }
    
    // Меняем язык в i18n
    i18n.changeLanguage(newLanguage);
    setCurrentLanguage(newLanguage);
  };

  return {
    currentLanguage,
    switchLanguage,
    isInitialized
  };
};
